import { useRecoilValue } from 'recoil';

import { CarrierLogoListNames } from '@/components/modules/carrier/logo-list-names';
import { EmissionsIndicator } from '@/components/modules/emissions/indicator';
import { JourneyRowTemplate } from '@/components/modules/journey/row-template';
import { JourneyTextBlock } from '@/components/modules/journey/text-block';
import { LineNameList } from '@/components/modules/line/name-list';
import { OptionBarSequence } from '@/components/modules/option/bar-sequence';
import { getOptionSequenceItems } from '@/components/modules/option/bar-sequence/utils';
import { OptionRowComponentProps, sharedOptionRowStyles } from '@/components/modules/option/row';
import { OptionViaBadge } from '@/components/modules/option/via-badge';
import { RateBadge } from '@/components/ui/rate-badge';
import { MODE_WATER } from '@/lib/constants';
import { locationsState } from '@/state/locations';
import { getSpotRatesByCarrierIdsState } from '@/state/rates';
import { formatDuration, formatFrequency } from '@/utils/format';
import { getOptionModelStopsListString, getOptionModelStopsShortString } from '@/utils/strings';
import { tv } from '@/utils/styles';

const OptionRowOcean = ({
  option,
  emissionsStats,
  onClickDetails,
  shouldShowRates,
  maxDurationRatio,
}: OptionRowComponentProps) => {
  const { totalDurationSeconds, frequency, emissions, carrierIds, via } = option;

  const locations = useRecoilValue(locationsState);
  const rates = useRecoilValue(getSpotRatesByCarrierIdsState(carrierIds));
  const sequenceItems = getOptionSequenceItems({ option, locations });
  const hasVia = !!(via && via.length);

  const { base } = styles({ showRates: shouldShowRates });
  const { bar, gridBlock } = sharedOptionRowStyles();

  return (
    <JourneyRowTemplate onClickDetails={onClickDetails}>
      <div className={base()}>
        <div className={gridBlock()}>
          <JourneyTextBlock
            primaryText={formatDuration(totalDurationSeconds)}
            subText={frequency ? formatFrequency(frequency) : ''}
          />
        </div>
        <div className={gridBlock()}>
          <JourneyTextBlock
            primaryText={getOptionModelStopsShortString({ option, mode: MODE_WATER })}
            subText={!hasVia ? getOptionModelStopsListString({ option, mode: MODE_WATER, locations }) : ''}
          />
          {hasVia && (
            <div className="-mt-[3px]">
              <OptionViaBadge via={via} isShort />
            </div>
          )}
        </div>
        <div className={gridBlock()}>
          <div className={bar()}>
            <OptionBarSequence sequenceItems={sequenceItems} ratio={maxDurationRatio} />
          </div>
          <div className="mt-2 px-4">
            <LineNameList lineIds={option.lineIds || []} carrierIds={carrierIds} />
          </div>
        </div>
        <div className={gridBlock()}>
          <CarrierLogoListNames carrierIds={option.carrierIds} mode={MODE_WATER} />
        </div>
        <div className={gridBlock({ withGap: true, center: true })}>
          <EmissionsIndicator
            mode={MODE_WATER}
            value={emissions?.average}
            unit={emissions?.unit}
            comparison={emissionsStats}
            wtwBreakdown={emissions?.wtw}
            averageMsg="Average for similar services"
          />
        </div>
        {shouldShowRates && (
          <div className={gridBlock({ withGap: true, center: true })}>
            <RateBadge rates={rates} />
          </div>
        )}
      </div>
    </JourneyRowTemplate>
  );
};

const styles = tv({
  slots: {
    base: 'relative grid grid-cols-[150px_140px_minmax(300px,2fr)_minmax(150px,1fr)_120px] leading-[14px]',
  },
  variants: {
    showRates: {
      true: 'grid-cols-[140px_140px_minmax(300px,2fr)_minmax(150px,1fr)_120px_100px]',
    },
  },
});

export { OptionRowOcean };
